import { useFormikContext } from 'formik'
import * as mui from '@mui/material'
import React from 'react'

import { langSwitchContext, translationsContext } from 'context/translations'
import { userContext } from 'context/user'
import { Calculate, UserClientCustomizations, UserLanguages } from 'types/graphql/schema'
import { valuesType } from 'utils/values'

import { FieldSoB } from '../components/FieldSoB'
import { TableSection } from '../components/TableSection'
import { formatUnits as fu, formatValue as f } from '../utils'

import logoMilkPay from './assets/milkpay.svg'


export function SectionShortTermReturns(props: {
  calculate: (values: valuesType) => void
  calculationResult: Calculate
  isPdf: boolean
}) {
  const t = React.useContext(translationsContext)
  const [lang, _] = React.useContext(langSwitchContext)
  const user = React.useContext(userContext)
  const v = props.calculationResult
  const {values} = useFormikContext<valuesType>()
  return <TableSection
    colorGroupText="white"
    colorGroupBg="#A70A2D"
    colorTableBg="#F9EDED"
    colorResultBg="#A70A2D"
    columns={[[
      '',
      t('values__units'),
      t('values__current'),
      t('values__adjusted'),
      t('values__difference'),
      {
        content: <b>{t('calc__milk__column_5')}</b>,
        cellProps: {colSpan: 3},
        // tooltip: t('calc__milk__column_5__tt'),
      },
    ]]}
    data={[
      [
        {content: t('calc__milk__1_1'), borderBottom: true},
        {...fu(`${t('units__'+v.weightUnits)}/${t('units__cow')}/${t('units__day')}`), borderBottom: true},
        {content: <FieldSoB calculate={props.calculate}  isPdf={props.isPdf} name="shortTermReturnsMilkCurrent" />, borderBottom: true},
        {...f(v.shortTermReturns.milkPerCowDayWithProduct), borderBottom: true},
        {content: <FieldSoB calculate={props.calculate}  isPdf={props.isPdf} name="shortTermReturnsMilkDifference" />, borderBottom: true},
        {
          content: <FieldSoB calculate={props.calculate}  isPdf={props.isPdf} name="shortTermReturnsMilkFmmoPrice" sx={{textAlign: 'right'}} />,
          cellProps: {sx: {px: 0, '& input': {textAlign: 'right'}}},
        },
        f(`${v.currency.sign}/${t('units__'+v.weightUnits)}`),
        {...f(t('calc__milk__1_8')), cellProps: {sx: {px: 0, textAlign: 'left'}}},
      ],
      [
        t('calc__milk__2_1'),
        fu('%'),
        {content: <FieldSoB calculate={props.calculate}  isPdf={props.isPdf} name="shortTermReturnsFatCurrent" />},
        f(v.shortTermReturns.fatPercent),
        {content: <FieldSoB calculate={props.calculate}  isPdf={props.isPdf} name="shortTermReturnsFatDifference" />},
        {
          content: <FieldSoB calculate={props.calculate}  isPdf={props.isPdf} name="shortTermReturnsFatFmmoPrice" sx={{textAlign: 'right'}} />,
          cellProps: {sx: {px: 0, '& input': {textAlign: 'right'}}},
        },
        f(`${v.currency.sign}/${t('units__'+v.weightUnits)}`),
        {...f(t('calc__milk__2_8')), cellProps: {sx: {px: 0, textAlign: 'left'}}},
      ],
      [
        t('calc__milk__3_1'),
        fu('%'),
        {content: <FieldSoB calculate={props.calculate}  isPdf={props.isPdf} name="shortTermReturnsProteinCurrent" />},
        f(v.shortTermReturns.proteinPercent),
        {content: <FieldSoB calculate={props.calculate}  isPdf={props.isPdf} name="shortTermReturnsProteinDifference" />},
        {
          content: <FieldSoB calculate={props.calculate}  isPdf={props.isPdf} name="shortTermReturnsProteinFmmoPrice" sx={{textAlign: 'right'}} />,
          cellProps: {sx: {px: 0, '& input': {textAlign: 'right'}}},
        },
        f(`${v.currency.sign}/${t('units__'+v.weightUnits)}`),
        {...f(t('calc__milk__3_8')), cellProps: {sx: {px: 0, textAlign: 'left'}}},
      ],
      [
        {content: t('calc__milk__4_1'), borderBottom: true},
        {...fu('%'), borderBottom: true},
        {content: <FieldSoB calculate={props.calculate}  isPdf={props.isPdf} name="shortTermReturnsOtherSolidsCurrent" />, borderBottom: true},
        {...f(v.shortTermReturns.otherSolidsPercent), borderBottom: true},
        {content: <FieldSoB calculate={props.calculate}  isPdf={props.isPdf} name="shortTermReturnsOtherSolidsDifference" />, borderBottom: true},
        {
          content: <FieldSoB calculate={props.calculate}  isPdf={props.isPdf} name="shortTermReturnsOtherSolidsFmmoPrice" sx={{textAlign: 'right'}} />,
          cellProps: {sx: {px: 0, '& input': {textAlign: 'right'}}},
        },
        f(`${v.currency.sign}/${t('units__'+v.weightUnits)}`),
        {...f(t('calc__milk__4_8')), cellProps: {sx: {px: 0, textAlign: 'left'}}},
      ],
      [
        {content: t('calc__milk__5_1'), borderBottom: true},
        {...fu(`${t('units__'+v.weightUnits)}/${t('units__cow')}/${t('units__day')}`), borderBottom: true},
        {...f(v.shortTermReturns.ecmPerCowDay.current), borderBottom: true},
        {...f(v.shortTermReturns.ecmPerCowDay.withProduct), borderBottom: true},
        {...f(v.shortTermReturns.ecmPerCowDay.difference), borderBottom: true},
        {
          content: <FieldSoB calculate={props.calculate}  isPdf={props.isPdf} name="shortTermReturnsPremiumFmmoPrice" sx={{textAlign: 'right'}} />,
          cellProps: {sx: {px: 0, '& input': {textAlign: 'right'}}},
        },
        {...f(`${v.currency.sign}/${v.weightUnits === 'lb' ? 'cwt' : t('units__kg')}`)},
        {
          content: <mui.Box>
            {f(t('calc__milk__5_8')).content}
            {v.weightUnits === 'lb' && !props.isPdf && user.data?.clientCustomization !== UserClientCustomizations.DEVENISH &&
              <mui.Box sx={{position: 'absolute', bottom: '-32px', right: '280px'}}>
                <a href="https://www.milkpay.com/" target="_blank">
                  <img src={logoMilkPay+'#svgView(preserveAspectRatio(none))'} alt="MilkPay" width="70" height="35"/>
                </a>
              </mui.Box>
            }
          </mui.Box>,
          cellProps: {sx: {px: 0, textAlign: 'left'}},
        },
      ],
      [
        t('calc__milk__6_1'),
        fu(`${t('units__'+v.weightUnits)}/${t('units__cow')}/${t('units__day')}`),
        f(v.shortTermReturns.fatPerCowDay.current, 3),
        f(v.shortTermReturns.fatPerCowDay.withProduct, 3),
        f(v.shortTermReturns.fatPerCowDay.difference, 3),
        '',
        '',
        ''
      ],
      [
        t('calc__milk__7_1'),
        fu(`${t('units__'+v.weightUnits)}/${t('units__cow')}/${t('units__day')}`),
        f(v.shortTermReturns.proteinPerCowDay.current, 3),
        f(v.shortTermReturns.proteinPerCowDay.withProduct, 3),
        f(v.shortTermReturns.proteinPerCowDay.difference, 3),
        '',
        '',
        '',
      ],
      [
        t('calc__milk__8_1'),
        fu(`${t('units__'+v.weightUnits)}/${t('units__cow')}/${t('units__day')}`),
        f(v.shortTermReturns.otherSolidsPerCowDay.current, 3),
        f(v.shortTermReturns.otherSolidsPerCowDay.withProduct, 3),
        f(v.shortTermReturns.otherSolidsPerCowDay.difference, 3),
        '',
        '',
        '',
      ],
      ...(
        lang === UserLanguages.en
        ? [[
            {content: t('calc__milk__9_1'), borderTop: true, cellProps: {rowSpan: 2}},
            {...fu(`${v.currency.sign}/cwt`), borderTop: true},
            {...f(v.shortTermReturns.impactCwtCost.current, 2), borderTop: true},
            {...f(v.shortTermReturns.impactCwtCost.withProduct, 2), borderTop: true},
            {...f(v.shortTermReturns.impactCwtCost.difference, 2), borderTop: true},
            '',
            '',
            '',
          ]]
        : []
      ),
      [
        ...(
          lang === UserLanguages.en
            ? []
            : [{content: t('calc__milk__9_1'), borderTop: true}]
        ),
        {...fu(`${v.currency.sign}/${t('units__cow')}/${t('units__day')}`), borderTop: lang !== UserLanguages.en},
        {...f(v.shortTermReturns.impactPerCowDayCost.current, 2), borderTop: lang !== UserLanguages.en},
        {...f(v.shortTermReturns.impactPerCowDayCost.withProduct, 2), borderTop: lang !== UserLanguages.en},
        {...f(v.shortTermReturns.impactPerCowDayCost.difference, 2), borderTop: lang !== UserLanguages.en},
        '',
        '',
        '',
      ],
      [
        {content: t('calc__milk__11_1'), borderTop: true},
        {...fu(`${v.currency.sign}/${t('units__cow')}/${t('units__year')}`), borderTop: true},
        {...f(v.shortTermReturns.impactPerCowYearCost.current, 2), borderTop: true},
        {...f(v.shortTermReturns.impactPerCowYearCost.withProduct, 2), borderTop: true},
        {...f(v.shortTermReturns.impactPerCowYearCost.difference, 2), borderTop: true},
        '',
        '',
        '',
      ],
    ]}
    dataRowsCollapsible={[5, 6, 7, 9, 10]}
    groups={[
      {content: t('calc__milk__title'), firstRow: 0},
    ]}
    groupsColumn=''
    isPdf={props.isPdf}
    result={{
      padding: '20px',
      title: t('calc__milk__result__title'),
      tooltip: t('calc__milk__result__tt'),
      subtitle: t('calc__milk__result__subtitle'),
      units: `${v.currency.sign}/${t('units__' + values.resultsUnitsAmount)}/${t('units__' + values.resultsUnitsPeriod)}`,
      value: f(v.shortTermReturns.totalImpactCost.difference, 2).text,
    }}
  />
}